/**
 * @file Negative Impacts Service
 * @description API endpoints for negative impacts (risks) operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { NegativeImpact, NegativeImpactWithCategory, AmountFrequency } from '@medsuite/models';

/**
 * Create negative impact DTO
 */
export interface CreateNegativeImpactDto {
  category_id: number;
  title: string;
  description?: string | null;
  amount?: number | null;
  amount_frequency?: AmountFrequency | null;
  clients_at_risk?: number | null;
  arr_at_risk?: number | null;
}

/**
 * Update negative impact DTO
 */
export interface UpdateNegativeImpactDto {
  category_id?: number;
  title?: string;
  description?: string | null;
  amount?: number | null;
  amount_frequency?: AmountFrequency | null;
  clients_at_risk?: number | null;
  arr_at_risk?: number | null;
}

/**
 * Batch update item with id (-1 or negative for new items)
 */
export interface NegativeImpactBatchItem {
  id: number;
  category_id: number;
  title: string;
  description?: string | null;
  amount?: number | null;
  amount_frequency?: AmountFrequency | null;
  clients_at_risk?: number | null;
  arr_at_risk?: number | null;
}

/**
 * Batch update request DTO
 */
export interface BatchUpdateNegativeImpactsDto {
  items: NegativeImpactBatchItem[];
  deleteIds?: number[];
}

/**
 * Negative impacts list response
 */
export interface NegativeImpactsListResponse {
  investment_id: number;
  negative_impacts: NegativeImpactWithCategory[];
  total: number;
}

/**
 * Batch update response
 */
export interface BatchUpdateResponse {
  investment_id: number;
  negative_impacts: NegativeImpactWithCategory[];
  changes: {
    created: number;
    updated: number;
    deleted: number;
  };
}

/**
 * Summary response
 */
export interface NegativeImpactsSummaryResponse {
  investment_id: number;
  summary: {
    count: number;
    totalAmount: number;
    totalClientsAtRisk: number;
    totalArrAtRisk: number;
  };
}

/**
 * Negative Impacts Service
 */
class NegativeImpactsService {
  /**
   * Get all negative impacts for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<NegativeImpactsListResponse>> {
    return apiClient.get<NegativeImpactsListResponse>(
      `/investments/${investmentId}/negative-impacts`
    );
  }

  /**
   * Create a new negative impact
   */
  async create(
    investmentId: number,
    data: CreateNegativeImpactDto
  ): Promise<ApiResponse<NegativeImpact>> {
    return apiClient.post<NegativeImpact>(
      `/investments/${investmentId}/negative-impacts`,
      data
    );
  }

  /**
   * Update a negative impact
   */
  async update(
    investmentId: number,
    negativeImpactId: number,
    data: UpdateNegativeImpactDto
  ): Promise<ApiResponse<NegativeImpact>> {
    return apiClient.put<NegativeImpact>(
      `/investments/${investmentId}/negative-impacts/${negativeImpactId}`,
      data
    );
  }

  /**
   * Delete a negative impact
   */
  async delete(investmentId: number, negativeImpactId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(
      `/investments/${investmentId}/negative-impacts/${negativeImpactId}`
    );
  }

  /**
   * Batch update negative impacts (create, update, delete in one request)
   */
  async batchUpdate(
    investmentId: number,
    data: BatchUpdateNegativeImpactsDto
  ): Promise<ApiResponse<BatchUpdateResponse>> {
    return apiClient.put<BatchUpdateResponse>(
      `/investments/${investmentId}/negative-impacts/batch`,
      data
    );
  }

  /**
   * Get summary statistics for negative impacts
   */
  async getSummary(investmentId: number): Promise<ApiResponse<NegativeImpactsSummaryResponse>> {
    return apiClient.get<NegativeImpactsSummaryResponse>(
      `/investments/${investmentId}/negative-impacts/summary`
    );
  }
}

export const negativeImpactsService = new NegativeImpactsService();
